import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  addDoc, 
  collection, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  Wand2, 
  BookOpen, 
  Heart,
  Mail,
  Sparkles,
  CheckCircle
} from 'lucide-react';

const StudyCounseling = ({ userId, userName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [message, setMessage] = useState('');
  const [contactInfo, setContactInfo] = useState('');
  const [contactType, setContactType] = useState('email');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const counselor = {
    name: 'Abhi',
    role: 'Counsellor',
    bgColor: 'bg-purple-50',
    darkBgColor: 'dark:bg-purple-900/20',
    textColor: 'text-purple-600',
    darkTextColor: 'dark:text-purple-400',
    greeting: 'Hey there! 👋',
    message: 'I\'m here to help you with your studies. Whether you\'re struggling with a difficult subject, feeling overwhelmed with exams, or just need some guidance - I\'m here for you!',
    icon: Wand2
  };

  const handleSubmit = async () => {
    if (!message.trim() || !contactInfo.trim()) return;

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'helpRequests'), {
        userId: userId,
        userName: userName,
        message: message,
        contactInfo: contactInfo,
        contactType: contactType,
        assignedCounselor: counselor.name,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      setSubmitted(true);
      setTimeout(() => {
        setIsOpen(false);
        setCurrentStep(1);
        setMessage('');
        setContactInfo('');
        setSubmitted(false);
      }, 4000);
    } catch (error) {
      // Error submitting help request
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 1 && message.trim()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && contactInfo.trim()) {
      handleSubmit();
    }
  };

  const canProceed = () => {
    if (currentStep === 1) return message.trim().length > 0;
    if (currentStep === 2) return contactInfo.trim().length > 0;
    return false;
  };

  return (
    <div className="h-full flex flex-col p-6 sm:p-8">
      {/* Enhanced Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl shadow-lg">
            <Wand2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">MahStry counselling</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Expert guidance for your studies</p>
          </div>
        </div>
        <button
          onClick={() => setIsOpen(true)}
          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-violet-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          Get Help
        </button>
      </div>

      {/* Enhanced Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center text-center">
        <div className="relative mb-8">
          <div className="w-28 h-28 bg-gradient-to-br from-purple-400 to-violet-500 rounded-full flex items-center justify-center shadow-2xl mb-6 transform hover:scale-110 transition-transform duration-300">
            <Wand2 className="w-14 h-14 text-white" />
          </div>
          <div className="absolute -top-3 -right-3 w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
        </div>
        
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Need guidance in your studies?
        </h3>
        <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed max-w-md">
          I'm here to help you with your studies! Whether you're struggling with a difficult subject, 
          feeling overwhelmed with exams, or just need some expert guidance, I'm ready to guide you through your academic journey.
        </p>
        
        <div className="grid grid-cols-2 gap-4 w-full max-w-sm mb-8">
          <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-xl p-5 border border-purple-100 dark:border-purple-800">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-600 rounded-lg flex items-center justify-center mx-auto mb-3">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <p className="text-sm font-medium text-purple-700 dark:text-purple-300">Academic Support</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-5 border border-blue-100 dark:border-blue-800">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Mental Wellness</p>
          </div>
        </div>
      </div>

      {/* Enhanced Footer */}
      <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
        <div className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            Available 24/7 • Confidential • Free
          </p>
          <div className="flex items-center justify-center space-x-2">
            <div className="w-3 h-3 bg-purple-400 rounded-full animate-pulse"></div>
            <div className="w-3 h-3 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-3 h-3 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                {!submitted ? (
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Wand2 className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                        MahStry counselling
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Step {currentStep} of 2
                      </p>
                    </div>

                    {/* Step 1: Message */}
                    {currentStep === 1 && (() => {
                      const IconComponent = counselor.icon;
                      return (
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="space-y-4"
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0">
                            <div 
                              className={`w-12 h-12 rounded-full border-2 border-purple-200 dark:border-purple-700 ${counselor.bgColor} ${counselor.darkBgColor} flex items-center justify-center`}
                            >
                              <IconComponent className={`w-6 h-6 ${counselor.textColor} ${counselor.darkTextColor}`} />
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                {counselor.name}
                              </p>
                              <span className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300 px-2 py-1 rounded-full">
                                {counselor.role}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                              {counselor.greeting}
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                              {counselor.message}
                            </p>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Tell us about your concern:
                          </label>
                          <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Describe what you're struggling with..."
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm resize-none"
                            rows={4}
                          />
                        </div>
                      </motion.div>
                      );
                    })()}

                    {/* Step 2: Contact Info */}
                    {currentStep === 2 && (
                      <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="space-y-4"
                      >
                        <div className="text-center">
                          <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Mail className="w-8 h-8 text-white" />
                          </div>
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            How can we reach you?
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            We'll get back to you within 24 hours
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Contact Method:
                          </label>
                          <select
                            value={contactType}
                            onChange={(e) => setContactType(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                          >
                            <option value="email">Email</option>
                            <option value="phone">Phone</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {contactType === 'email' ? 'Email Address:' : 'Phone Number:'}
                          </label>
                          <input
                            type={contactType === 'email' ? 'email' : 'tel'}
                            value={contactInfo}
                            onChange={(e) => setContactInfo(e.target.value)}
                            placeholder={contactType === 'email' ? 'your.email@example.com' : '+1 (555) 123-4567'}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                          />
                        </div>
                      </motion.div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex space-x-3">
                      {currentStep === 2 && (
                        <button
                          onClick={() => setCurrentStep(1)}
                          className="flex-1 px-4 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                          Back
                        </button>
                      )}
                      <button
                        onClick={handleNext}
                        disabled={!canProceed() || isSubmitting}
                        className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        {isSubmitting ? (
                          <div className="flex items-center justify-center space-x-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Submitting...</span>
                          </div>
                        ) : currentStep === 1 ? (
                          'Next'
                        ) : (
                          'Submit Request'
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      Request Submitted!
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Thank you for reaching out. {counselor.name} will review your request and get back to you within 24 hours.
                    </p>
                    <div className="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-xl p-4 border border-purple-100 dark:border-purple-800">
                      <p className="text-sm text-purple-700 dark:text-purple-300">
                        <strong>Request ID:</strong> {Math.random().toString(36).substr(2, 9).toUpperCase()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {!isOpen && (
        <div className="text-center py-6">
          <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Wand2 className="w-8 h-8 text-purple-600 dark:text-purple-400" />
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Need guidance in your studies? I'm here to help!
          </p>
        </div>
      )}
    </div>
  );
};

export default StudyCounseling;

